import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  Security as SecurityIcon,
  Star as StarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  PauseCircle as PauseCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategoryName, useCategoryDescription } from '../../utils/categoryTranslation';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getTokenAuth } from '../../utils/authUtils';
import useCompleteUser from '../../hooks/useCompleteUser';
import PERMISSIONS from '../../config/permissionsList';
import api from '../../services/api';
import ReadOnlyMap from '../common/ReadOnlyMap';
import ListingReviewsList from '../reviews/ListingReviewsList';
import BookingSettingsSummary from '../booking/BookingSettingsSummary';
// Composants pour les disponibilités
import NewAvailabilityManager from '../availability/NewAvailabilityManager';
import { fetchAvailability } from '../../services/availabilityService';
import RideShareMap from './RideShareMap';
import ListingDetailsView from './ListingDetailsView';

const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';
const getUploadedImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${UPLOADS_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
};

const ListingDetail = () => {
  const { t } = useTranslation();
  const getCategoryName = useCategoryName();
  const getCategoryDescription = useCategoryDescription();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showNotification } = useNotifications();
  const { formatPrice } = useCurrency();
  const { completeUser, hasPermission, loading: userLoading } = useCompleteUser();
  
  console.log('[ListingDetail] Component initializing');

  // Fonction pour formater le prix avec la devise de l'annonce (pas la devise préférée de l'utilisateur)
  const formatListingPrice = (price) => {
    console.log('[ListingDetail] formatListingPrice called with:', { 
      price, 
      currency: listing?.currency, 
      listingKeys: listing ? Object.keys(listing) : [],
      listing 
    });
    
    // Convertir en nombre et vérifier si c'est valide
    const numericPrice = Number(price);
    const isValidPrice = !isNaN(numericPrice) && numericPrice > 0;
    
    // Vérifier différents champs possibles pour la devise
    const currency = listing?.currency || listing?.listing_currency || 'EUR'; // Fallback vers EUR
    const hasCurrency = currency && currency.length === 3;
    
    if (!isValidPrice) {
      console.warn('[ListingDetail] Returning N/A because price is invalid:', { 
        priceExists: !!price,
        numericPrice,
        isValidPrice
      });
      return 'N/A';
    }
    
    console.log('[ListingDetail] Using currency:', currency, 'for price:', numericPrice);
    
    try {
      const result = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(numericPrice);
      console.log('[ListingDetail] Formatted price result:', result);
      return result;
    } catch (error) {
      // Si la devise n'est pas supportée, revenir au format par défaut
      console.warn('Unsupported currency for listing:', currency, error);
      return `${numericPrice} ${currency}`;
    }
  };
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [reserveDialogOpen, setReserveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  // Nouvel état pour le popup de motif de rejet
  const [rejectionPopupOpen, setRejectionPopupOpen] = useState(false);
  // Nouvel état pour le popup de motif de désactivation
  const [deactivationPopupOpen, setDeactivationPopupOpen] = useState(false);

  // États pour les disponibilités
  const [availabilityPeriods, setAvailabilityPeriods] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Prix par créneau (mode time_slot / time_slot_capacity)
  // Fallback sur base_price si utilisé par d'autres flows
  const slotPrice = listing?.booking_settings?.price_per_slot || listing?.booking_settings?.base_price;

  // Fonctions de navigation pour les images
  const goToPreviousImage = () => {
    if (listing.images && listing.images.length > 1) {
      setSelectedImage(prev => prev === 0 ? listing.images.length - 1 : prev - 1);
    }
  };

  const goToNextImage = () => {
    if (listing.images && listing.images.length > 1) {
      setSelectedImage(prev => prev === listing.images.length - 1 ? 0 : prev + 1);
    }
  };
  
  // État pour les accordéons (tous ouverts par défaut pour une meilleure UX)
  const [expandedPanels, setExpandedPanels] = useState({
    location: true,
    pricing: true,
    owner: true,
    category: true,
    details: true,
    address: true,
    availability: true,
    reviews: true,
    listingDetails: true
  });

  // nettoie rejection_reason en retirant les préfixes entre crochets
  const cleanRejectionReason = (reason) => {
    if (!reason || typeof reason !== 'string') return '';
    return reason
      .replace(/\[PERMANENT\]/g, '')
      .replace(/\[TEMPORARY\]/g, '')
      .replace(/\[SELF_SUSPENDED\]/g, '')
      .trim();
  };

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'default';
      case 'pending_verification':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Fonction pour obtenir le texte traduit du statut
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return t('listing.status.active');
      case 'draft':
        return t('listing.status.draft');
      case 'pending_verification':
        return t('listing.status.pendingVerification');
      case 'rejected':
        return t('listing.status.rejected');
      case 'inactive':
        return t('listing.status.inactive');
      default:
        return status;
    }
  };

  useEffect(() => {
    console.log('[ListingDetail] useEffect triggered:', { 
      id, 
      refreshed: location.state?.refreshed,
      pathname: location.pathname,
      state: location.state 
    });
    fetchListing();
  }, [id, location.pathname, location.state?.refreshed]);

  // Navigation au clavier pour les images
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (listing?.images && listing.images.length > 1) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goToPreviousImage();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          goToNextImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [listing?.images, selectedImage]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      console.log('[ListingDetail] Fetching listing details...', { id, refreshed: location.state?.refreshed });

      // Toujours ajouter un timestamp pour contourner le cache lors du premier chargement
      // et surtout quand on vient d'une modification
      const timestamp = new Date().getTime();
      const url = `/listings/${id}?_t=${timestamp}`;
      
      console.log('[ListingDetail] Making API request to:', url, '(with cache bypass)');

      const response = await api.get(url);
      const data = response.data;
      
      console.log('[ListingDetail] Fresh listing data loaded:', data);
      console.log('[ListingDetail] Listing status:', data.listing?.status);
      console.log('[ListingDetail] Listing coordinates:', { 
        lat: data.listing?.latitude, 
        lon: data.listing?.longitude 
      });
      console.log('[ListingDetail] Has rejection reason?', !!data.listing?.rejection_reason);
      if (data.listing?.rejection_reason) {
        console.log('[ListingDetail] Rejection reason content:', data.listing.rejection_reason);
      }

      setListing(data.listing);
      
      // Charger les disponibilités après avoir chargé le listing
      if (data.listing?.id) {
        await fetchAvailabilityData(data.listing.id);
      }
      
      // Nettoyer le state de navigation après avoir récupéré les données fraîches
      if (location.state?.refreshed) {
        console.log('[ListingDetail] Clearing navigation state after fresh data load');
        window.history.replaceState({}, '', window.location.pathname);
      }
      
    } catch (error) {
      console.error('[ListingDetail] Error fetching listing:', error);
      showNotification(t('listing.errors.notFound'), 'error');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les disponibilités
  const fetchAvailabilityData = async (listingId) => {
    try {
      setAvailabilityLoading(true);
      console.log('[ListingDetail] Fetching availability for listing:', listingId);
      
      const periods = await fetchAvailability(listingId);
      setAvailabilityPeriods(periods || []);
      
      console.log('[ListingDetail] Availability periods loaded:', periods);
    } catch (error) {
      console.error('[ListingDetail] Error fetching availability:', error);
      setAvailabilityPeriods([]);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleContact = () => {
    setContactDialogOpen(true);
  };

  const handleReserve = () => {
    setReserveDialogOpen(true);
  };

  const handleEdit = () => {
    navigate(`/edit-listing/${id}`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const response = await api.delete(`/listings/${id}`);

      showNotification(t('listing.success.deleted'), 'success');
      navigate('/my-listings');
    } catch (error) {
      console.error('Error deleting listing:', error);
      showNotification(t('listing.errors.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return t('common.notAvailable');
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return t('common.notAvailable');
      }
      
      // Format français : jour/mois/année
      const options = { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit'
      };
      
      return date.toLocaleDateString('fr-FR', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('common.notAvailable');
    }
  };

  const isOwner = user && listing?.owner?.id === user?.id;
  
  // Logique robuste pour déterminer si l'utilisateur peut modifier/supprimer l'annonce
  const canEditListing = useMemo(() => {
    if (!user || !listing || !user.id) {
      return false;
    }
    
    // Le propriétaire peut toujours modifier
    if (listing.owner?.id && listing.owner.id === user.id) {
      return true;
    }
    
    // Utilisateurs avec permission de modifier n'importe quelle annonce
    if (PERMISSIONS && hasPermission && PERMISSIONS.UPDATE_ANY_ITEM) {
      const hasUpdatePermission = hasPermission(PERMISSIONS.UPDATE_ANY_ITEM);
      return hasUpdatePermission;
    }
    
    return false;
  }, [user, listing, hasPermission]);
  
  const canDeleteListing = useMemo(() => {
    if (!user || !listing || !user.id) {
      return false;
    }
    
    // Le propriétaire peut toujours supprimer
    if (listing.owner?.id && listing.owner.id === user.id) {
      return true;
    }
    
    // Utilisateurs avec permission de supprimer n'importe quelle annonce
    if (PERMISSIONS && hasPermission && PERMISSIONS.DELETE_ANY_ITEM) {
      const hasDeletePermission = hasPermission(PERMISSIONS.DELETE_ANY_ITEM);
      return hasDeletePermission;
    }
    
    return false;
  }, [user, listing, hasPermission]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!listing) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          {t('listing.errors.notFound')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          {t('common.goBack')}
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: 1200, 
        mx: 'auto', 
        p: 3,
        boxSizing: 'border-box',
        overflow: 'hidden' // Empêche le débordement horizontal
      }}
    >
      {/* Header avec bouton de retour */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/my-listings')}
            variant="outlined"
            sx={{
              mr: 2,
              borderColor: '#6200ee',
              color: '#6200ee',
              fontWeight: 'medium',
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(98, 0, 238, 0.1)',
              '&:hover': {
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                borderColor: 'transparent',
                boxShadow: '0 4px 8px rgba(98, 0, 238, 0.3)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('common.backToListings')}
          </Button>
        </Box>
        
        <Typography variant="h4" gutterBottom>
          {listing.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Chip
            label={getStatusText(listing.status)}
            color={getStatusColor(listing.status)}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {t('listing.detail.createdAt')}: {formatDate(listing.created_at || listing.createdAt)}
          </Typography>
        </Box>
      </Box>

      {/* Section de notification de rejet - Visible si l'annonce est rejetée */}
      {listing.status === 'rejected' && (
        <Card
          sx={{ 
            mb: 3,
            border: '2px solid #f44336',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(244, 67, 54, 0.15)',
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffebee 100%)',
            position: 'relative',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 36px rgba(244, 67, 54, 0.25)',
            },
          }}
          onClick={() => setRejectionPopupOpen(true)}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <CancelIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('listing.detail.rejectionNotice.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('listing.detail.rejectionNotice.description')}
              </Typography>
            </Box>
            
            {/* Indicateur pour inciter au clic */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              animation: 'pulse 1.5s infinite'
            }}>
              <Typography variant="body2" sx={{ mr: 1, fontWeight: 'medium' }}>
                {t('listing.detail.rejectionNotice.viewDetails')}
              </Typography>
              <ExpandMoreIcon sx={{ 
                fontSize: 24,
                animation: 'bounce 1s infinite alternate',
                '@keyframes bounce': {
                  '0%': { transform: 'translateY(0)' },
                  '100%': { transform: 'translateY(5px)' }
                }
              }} />
            </Box>
            
            <style jsx>{`
              @keyframes pulse {
                0% { opacity: 0.7; }
                50% { opacity: 1; }
                100% { opacity: 0.7; }
              }
            `}</style>
          </Box>
        </Card>
      )}

      {/* Section de notification de désactivation - Visible si l'annonce est désactivée */}
      {listing.status === 'inactive' && (
        <Card
          sx={{ 
            mb: 3,
            border: '2px solid #ff9800',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(255, 152, 0, 0.15)',
            background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
            position: 'relative',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 36px rgba(255, 152, 0, 0.25)',
            },
          }}
          onClick={() => setDeactivationPopupOpen(true)}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <PauseCircleIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('listing.detail.deactivationNotice.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('listing.detail.deactivationNotice.description')}
              </Typography>
            </Box>
            
            {/* Indicateur pour inciter au clic */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              animation: 'pulse 1.5s infinite'
            }}>
              <Typography variant="body2" sx={{ mr: 1, fontWeight: 'medium' }}>
                {t('listing.detail.deactivationNotice.viewDetails')}
              </Typography>
              <ExpandMoreIcon sx={{ 
                fontSize: 24,
                animation: 'bounce 1s infinite alternate',
                '@keyframes bounce': {
                  '0%': { transform: 'translateY(0)' },
                  '100%': { transform: 'translateY(5px)' }
                }
              }} />
            </Box>
            
            <style jsx>{`
              @keyframes pulse {
                0% { opacity: 0.7; }
                50% { opacity: 1; }
                100% { opacity: 0.7; }
              }
            `}</style>
          </Box>
        </Card>
      )}
      
      {/* Popup modal pour le motif de rejet */}
      <Dialog 
        open={rejectionPopupOpen} 
        onClose={() => setRejectionPopupOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 500
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          p: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CancelIcon sx={{ mr: 2, fontSize: 36 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {t('listing.detail.rejectionNotice.dialogTitle')}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ opacity: 0.9, ml: 0.5 }}>
            {t('listing.detail.rejectionNotice.dialogDescription')}
          </Typography>
        </Box>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ 
              color: '#d32f2f', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}>
              <WarningIcon sx={{ mr: 1 }} />
              {t('listing.detail.rejectionReason')}
            </Typography>
            
            <Paper elevation={0} sx={{
              p: 3,
              backgroundColor: '#fff3e0',
              border: '1px solid #ffcc80',
              borderRadius: 2,
              borderLeft: '4px solid #ff9800',
              mb: 3,
              position: 'relative',
              animation: 'fadeIn 0.6s ease-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 'medium',
                color: '#e65100',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {cleanRejectionReason(listing.rejection_reason) ||
                  t('listing.detail.defaultRejectionMessage')}
              </Typography>
            </Paper>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              mt: 3
            }}>
              <InfoIcon sx={{ mr: 1 }} />
              {t('listing.detail.nextSteps')}
            </Typography>
            
            <List dense>
              {[
                t('listing.detail.rejectionNextStep1'),
                t('listing.detail.rejectionNextStep2'),
                t('listing.detail.rejectionNextStep3')
              ].map((text, index) => (
                <ListItem key={index} sx={{ 
                  pl: 0,
                  animation: `slideIn 0.5s ${index * 0.2}s forwards`,
                  opacity: 0,
                  transform: 'translateX(-10px)',
                  '@keyframes slideIn': {
                    '0%': { opacity: 0, transform: 'translateX(-10px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' }
                  }
                }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}>
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={text}
                    primaryTypographyProps={{
                      sx: { fontWeight: 'medium' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => window.open('mailto:support@krillou.com?subject=Question sur le rejet de mon annonce', '_blank')}
            startIcon={<EmailIcon />}
            sx={{
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            {t('common.contactSupport')}
          </Button>

          <Box>
            <Button
              onClick={() => setRejectionPopupOpen(false)}
              sx={{ mr: 1 }}
            >
              {t('common.close')}
            </Button>
            {canEditListing && (
              <Button
                variant="contained"
                onClick={() => {
                  setRejectionPopupOpen(false);
                  handleEdit();
                }}
                startIcon={<EditIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                  }
                }}
              >
                {t('listing.actions.edit')}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Popup modal pour le motif de désactivation */}
      <Dialog 
        open={deactivationPopupOpen} 
        onClose={() => setDeactivationPopupOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 500
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          p: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PauseCircleIcon sx={{ mr: 2, fontSize: 36 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {t('listing.detail.deactivationNotice.dialogTitle')}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ opacity: 0.9, ml: 0.5 }}>
            {t('listing.detail.deactivationNotice.dialogDescription')}
          </Typography>
        </Box>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ 
              color: '#f57c00', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}>
              <BlockIcon sx={{ mr: 1 }} />
              {t('listing.detail.deactivationReason')}
            </Typography>
            
            <Paper elevation={0} sx={{
              p: 3,
              backgroundColor: '#fff3e0',
              border: '1px solid #ffcc80',
              borderRadius: 2,
              borderLeft: '4px solid #ff9800',
              mb: 3,
              position: 'relative',
              animation: 'fadeIn 0.6s ease-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 'medium',
                color: '#e65100',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {cleanRejectionReason(listing.rejection_reason) ||
                  t('listing.detail.defaultDeactivationMessage')}
              </Typography>
            </Paper>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              mt: 3
            }}>
              <InfoIcon sx={{ mr: 1 }} />
              {t('listing.detail.nextSteps')}
            </Typography>
            
            <List dense>
              {[
                t('listing.detail.deactivationNextStep1'),
                t('listing.detail.deactivationNextStep2'),
                t('listing.detail.deactivationNextStep3')
              ].map((text, index) => (
                <ListItem key={index} sx={{ 
                  pl: 0,
                  animation: `slideIn 0.5s ${index * 0.2}s forwards`,
                  opacity: 0,
                  transform: 'translateX(-10px)',
                  '@keyframes slideIn': {
                    '0%': { opacity: 0, transform: 'translateX(-10px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' }
                  }
                }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}>
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={text}
                    primaryTypographyProps={{
                      sx: { fontWeight: 'medium' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => window.open('mailto:support@krillou.com?subject=Question sur la désactivation de mon annonce', '_blank')}
            startIcon={<EmailIcon />}
            sx={{
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            {t('common.contactSupport')}
          </Button>

          <Box>
            <Button
              onClick={() => setDeactivationPopupOpen(false)}
              sx={{ mr: 1 }}
            >
              {t('common.close')}
            </Button>
            {canEditListing && (
              <Button
                variant="contained"
                onClick={() => {
                  setDeactivationPopupOpen(false);
                  handleEdit();
                }}
                startIcon={<EditIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                  }
                }}
              >
                {t('listing.actions.edit')}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
        {/* Main Content */}
        <Grid item xs={12} md={8} sx={{ pl: { xs: 0, md: 2 }, pr: { xs: 0, md: 1 } }}>
          {/* Images */}
          <Card sx={{ mb: 3, position: 'relative' }}>
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                height="400"
                image={getUploadedImageUrl(listing.images?.[selectedImage]?.url) || '/assets/placeholder-equipment.svg'}
                alt={listing.title}
                sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
              />
              
              {/* Flèches de navigation */}
              {listing.images && listing.images.length > 1 && (
                <>
                  {/* Flèche gauche */}
                  <IconButton
                    onClick={goToPreviousImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      },
                      zIndex: 1,
                      width: 48,
                      height: 48
                    }}
                  >
                    <ChevronLeftIcon fontSize="large" />
                  </IconButton>
                  
                  {/* Flèche droite */}
                  <IconButton
                    onClick={goToNextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      },
                      zIndex: 1,
                      width: 48,
                      height: 48
                    }}
                  >
                    <ChevronRightIcon fontSize="large" />
                  </IconButton>
                  
                  {/* Indicateur de position */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.875rem',
                      fontWeight: 'medium'
                    }}
                  >
                    {selectedImage + 1} / {listing.images.length}
                  </Box>
                </>
              )}
            </Box>
            
            {/* Miniatures */}
            {listing.images && listing.images.length > 1 && (
              <Box sx={{ p: 2, display: 'flex', gap: 1, overflow: 'auto' }}>
                {listing.images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: index === selectedImage ? '3px solid #6200ee' : '1px solid #ddd',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                      }
                    }}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img
                      src={getUploadedImageUrl(image.url)}
                      alt={`${listing.title} ${index + 1}`}
                      style={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    {index === selectedImage && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(98, 0, 238, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: '#6200ee',
                            border: '2px solid white'
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Card>

          {/* Description */}
          <Accordion 
            expanded={expandedPanels.details} 
            onChange={handlePanelChange('details')}
            sx={{ 
              mb: 3,
              boxShadow: 2,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                📝 {t('listing.detail.details')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {listing.description}
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Détails spécifiques */}
          <ListingDetailsView
            listing={listing}
            expanded={expandedPanels.listingDetails}
            onChange={handlePanelChange('listingDetails')}
          />

          {/* Paramètres de réservation */}
          {listing?.booking_mode && (
            <BookingSettingsSummary listing={listing} variant="accordion" defaultExpanded />
          )}

          {/* Horaires début / fin — pour tous les listings en mode date_range */}
          {listing?.booking_mode === 'date_range' && (listing?.booking_settings?.checkin_time || listing?.booking_settings?.checkout_time) && (
            <Accordion
              sx={{
                mb: 3,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {listing.offer_type === 'property'
                    ? `🏠 ${t('listing:property.section')}`
                    : `⏰ ${t('listing:dateRange.section')}`
                  }
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 500 }}>
                        {listing.offer_type === 'property'
                          ? t('listing:property.checkinLabel')
                          : t('listing:dateRange.startLabel')
                        }
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#6200ee', fontWeight: 'bold', mt: 1 }}>
                        {listing.booking_settings?.checkin_time || (listing.offer_type === 'property' ? '14:00' : '09:00')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 500 }}>
                        {listing.offer_type === 'property'
                          ? t('listing:property.checkoutLabel')
                          : t('listing:dateRange.endLabel')
                        }
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#03dac4', fontWeight: 'bold', mt: 1 }}>
                        {listing.booking_settings?.checkout_time || (listing.offer_type === 'property' ? '12:00' : '18:00')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Section Disponibilités */}
          <Accordion 
            expanded={expandedPanels.availability} 
            onChange={handlePanelChange('availability')}
            sx={{ 
              mb: 3,
              boxShadow: 2,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <CalendarIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('availabilityManager.manageTitle', 'Gestion des disponibilités')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {availabilityLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>
                    {t('availabilityManager.loading', 'Chargement des disponibilités...')}
                  </Typography>
                </Box>
              ) : (
                <NewAvailabilityManager
                  listingId={listing?.id}
                  availabilityPeriods={availabilityPeriods}
                  bookingMode={listing?.booking_mode}
                  capacityDefault={listing?.capacity_default}
                  readOnly={true}
                />
              )}
            </AccordionDetails>
          </Accordion>

          {/* Adresse avec carte interactive */}
          <Accordion 
            expanded={expandedPanels.address} 
            onChange={handlePanelChange('address')}
            sx={{ 
              mb: 3,
              boxShadow: 2,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <LocationIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('listing.detail.address')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {/* Informations d'adresse */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                  {listing.address}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {listing.city}{listing.postalCode && `, ${listing.postalCode}`}
                </Typography>
                {listing.region && (
                  <Typography variant="body2" color="text.secondary">
                    {listing.region}{listing.country && `, ${listing.country}`}
                  </Typography>
                )}
              </Box>

              {/* Carte en lecture seule */}
              {listing.offer_type === 'ride_share' ? (
                <>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  {t('listing.address.rideShareMapTitle', 'Carte du trajet')}
                </Typography>
                <RideShareMap 
                   startPosition={listing.latitude && listing.longitude ? [listing.latitude, listing.longitude] : null}
                   endPosition={listing.booking_settings?.destinationLat && listing.booking_settings?.destinationLng ? [listing.booking_settings.destinationLat, listing.booking_settings.destinationLng] : null}
                   readOnly={true}
                   height={300}
                />
                {(listing.booking_settings?.destinationAddress) && (
                   <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                     <strong>{t('listing.map.arrival', 'Arrivée')}:</strong> {listing.booking_settings.destinationAddress}
                   </Typography>
                )}
                </>
              ) : (
                <ReadOnlyMap
                  latitude={listing.latitude}
                  longitude={listing.longitude}
                  address={listing.address}
                  height={250}
                  zoom={16}
                  showAddress={false}
                />
              )}
              
              {/* Note informative */}
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block', 
                  mt: 1, 
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}
              >
                ℹ️ Cette localisation est approximative pour protéger la vie privée
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Section Avis & Évaluations */}
          <Accordion
            expanded={expandedPanels.reviews}
            onChange={handlePanelChange('reviews')}
            sx={{
              mb: 3,
              boxShadow: 3,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <StarIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('listing.detail.reviewsTitle', 'Avis des locataires')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <ListingReviewsList listingId={listing?.id} initialLimit={5} />
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4} sx={{ pl: { xs: 0, md: 1 }, pr: { xs: 0, md: 2 } }}>
          {/* Localisation rapide - PREMIER */}
          {listing.latitude && listing.longitude && (
            <Accordion 
              expanded={expandedPanels.location} 
              onChange={handlePanelChange('location')}
              sx={{ 
                mb: 2,
                boxShadow: 3,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                  }
                }}
              >
                <LocationIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  Localisation
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <ReadOnlyMap
                  latitude={listing.latitude}
                  longitude={listing.longitude}
                  address={listing.city}
                  height={180}
                  zoom={14}
                  showAddress={false}
                />
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mt: 1, textAlign: 'center', fontWeight: 'medium' }}
                >
                  📍 {listing.city}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}



          {/* Tarification */}
          <Accordion 
            expanded={expandedPanels.pricing} 
            onChange={handlePanelChange('pricing')}
            sx={{ 
              mb: 2,
              boxShadow: 3,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <EuroIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('listing.form.pricing')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              {/* Vérifier s'il y a des prix disponibles */}
              {(listing.priceHourly || listing.price_hourly || 
                listing.priceDaily || listing.price_daily || 
                listing.priceWeekly || listing.price_weekly || 
                  listing.priceMonthly || listing.price_monthly || 
                  slotPrice) ? (
                <List dense>
                    {slotPrice && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          🕒
                        </ListItemIcon>
                        <ListItemText
                          primary={`${formatListingPrice(slotPrice)} / ${t('listing.detail.perSlot', 'créneau')}`}
                          sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                        />
                      </ListItem>
                    )}
                  {(listing.priceHourly || listing.price_hourly) && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        ⏰
                      </ListItemIcon>
                      <ListItemText
                        primary={`${formatListingPrice(listing.priceHourly || listing.price_hourly)} / ${t('listing.detail.perHour')}`}
                        sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                      />
                    </ListItem>
                  )}
                  {(listing.priceDaily || listing.price_daily) && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        📅
                      </ListItemIcon>
                      <ListItemText
                        primary={`${formatListingPrice(listing.priceDaily || listing.price_daily)} / ${t('listing.detail.perDay')}`}
                        sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                      />
                    </ListItem>
                  )}
                  {(listing.priceWeekly || listing.price_weekly) && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        📊
                      </ListItemIcon>
                      <ListItemText
                        primary={`${formatListingPrice(listing.priceWeekly || listing.price_weekly)} / ${t('listing.detail.perWeek')}`}
                        sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                      />
                    </ListItem>
                  )}
                  {(listing.priceMonthly || listing.price_monthly) && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        📆
                      </ListItemIcon>
                      <ListItemText
                        primary={`${formatListingPrice(listing.priceMonthly || listing.price_monthly)} / ${t('listing.detail.perMonth')}`}
                        sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                      />
                    </ListItem>
                  )}
                </List>
              ) : (
                /* Affichage quand aucun prix n'est défini */
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 3,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px dashed #ddd'
                }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 'medium' }}>
                    💰 {t('listing.detail.priceOnRequest')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('listing.detail.contactOwnerForPricing')}
                  </Typography>
                </Box>
              )}

              {/* Section caution si elle existe */}
              {(listing.depositAmount || listing.deposit_amount) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ mr: 1, fontSize: 'small' }} />
                    {t('listing.detail.deposit')}: {formatListingPrice(listing.depositAmount || listing.deposit_amount)}
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Propriétaire */}
          <Accordion 
            expanded={expandedPanels.owner} 
            onChange={handlePanelChange('owner')}
            sx={{ 
              mb: 2,
              boxShadow: 3,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('listing.detail.owner')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                  {listing.owner?.name?.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {listing.owner?.name || t('common.anonymous')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('listing.detail.memberSince')} {formatDate(
                      listing.owner?.createdAt || 
                      listing.owner?.created_at || 
                      listing.created_at || 
                      listing.createdAt
                    )}
                  </Typography>
                </Box>
              </Box>

              {!isOwner && user && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleContact}
                    startIcon={<EmailIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    {t('listing.detail.contact')}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleReserve}
                    startIcon={<CalendarIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    {t('listing.detail.reserve')}
                  </Button>
                </Box>
              )}

              {(canEditListing || canDeleteListing) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {isOwner 
                      ? t('listing.detail.yourListing') 
                      : t('listing.detail.adminActions')
                    }
                  </Typography>
                  
                  {canEditListing && (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleEdit}
                      startIcon={<EditIcon />}
                      sx={{
                        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                        color: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                        }
                      }}
                    >
                      {isOwner 
                        ? t('listing.actions.edit') 
                        : t('listing.detail.editAdmin')
                      }
                    </Button>
                  )}
                  
                  {/* Bouton de rafraîchissement pour déboguer */}
                  {isOwner && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        console.log('[ListingDetail] Manual refresh requested');
                        fetchListing();
                      }}
                      startIcon={<RefreshIcon />}
                      sx={{
                        borderColor: '#2196f3',
                        color: '#2196f3',
                        '&:hover': {
                          borderColor: '#1976d2',
                          backgroundColor: 'rgba(33, 150, 243, 0.04)'
                        }
                      }}
                    >
                      Rafraîchir
                    </Button>
                  )}
                  
                  {canDeleteListing && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleDeleteClick}
                      startIcon={<DeleteIcon />}
                      color="error"
                      sx={{
                        borderColor: '#f44336',
                        color: '#f44336',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: '#d32f2f',
                          backgroundColor: 'rgba(244, 67, 54, 0.04)'
                        }
                      }}
                    >
                      {isOwner 
                        ? t('listing.actions.delete') 
                        : t('listing.detail.deleteAdmin')
                      }
                    </Button>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Catégorie */}
          <Accordion 
            expanded={expandedPanels.category} 
            onChange={handlePanelChange('category')}
            sx={{ 
              mb: 2,
              boxShadow: 3,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                🏷️ {t('listing.detail.category')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Chip
                label={listing.categoryData ? getCategoryName(listing.categoryData) : listing.category || t('listing.detail.noCategory')}
                color="primary"
                variant="outlined"
                sx={{ 
                  fontSize: '1rem',
                  height: 'auto',
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  '& .MuiChip-label': {
                    fontWeight: 'medium'
                  }
                }}
              />
              {listing.categoryData?.description && (
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  {getCategoryDescription(listing.categoryData)}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)}>
        <DialogTitle>
          {t('listing.detail.contact')} {listing?.owner?.name || t('common.anonymous')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('listing.detail.contactFeatureComingSoon')}
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <EmailIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('common.email')}
                secondary={listing?.owner?.email || t('common.notAvailable')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PhoneIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('common.phone')}
                secondary={listing?.owner?.phone || t('common.notAvailable')}
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reserve Dialog */}
      <Dialog open={reserveDialogOpen} onClose={() => setReserveDialogOpen(false)}>
        <DialogTitle>
          {t('listing.detail.reserve')} {listing.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('listing.detail.reserveFeatureComingSoon')}
          </Typography>
          <Alert severity="info">
            {t('listing.detail.reservationUnderDevelopment')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReserveDialogOpen(false)}>
            {t('common.close')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setReserveDialogOpen(false);
              handleContact();
            }}
          >
            {t('listing.detail.contact')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          {t('listing.actions.deleteConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('listing.actions.deleteWarning')}
          </Alert>
          <Typography variant="body1" paragraph>
            {t('listing.actions.deleteConfirmMessage', { title: listing?.title })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('listing.actions.deleteIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? t('common.deleting') : t('listing.actions.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListingDetail;
